import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { login as apiLogin, logout as apiLogout, refresh as apiRefresh } from '../services/authApi.js';
import { setAccessToken, setUnauthorizedHandler } from '../services/apiClient.js';

const STORAGE_KEY = 'recica.auth.session';

export const AuthContext = createContext(null);

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistSession(session) {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

// Refresh tokens are single-use (rotation). React StrictMode double-runs the
// bootstrap effect, so two refresh calls would race and the replayed token
// gets a 401 → forced logout. Dedupe in-flight refreshes per token so both
// effect runs share one request. The rotated session is persisted right in
// the success path (not only via React state) so a cancelled/unmounted run
// can never lose the rotated token. The cache is cleared once settled, so a
// later mount always re-validates against the server (revocation respected).
let inflightRefresh = null;

function dedupedRefresh(refreshToken) {
  if (inflightRefresh?.token === refreshToken) {
    return inflightRefresh.promise;
  }
  const promise = apiRefresh(refreshToken).then((result) => {
    persistSession({
      user: result.user,
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
    });
    return result;
  });
  inflightRefresh = { token: refreshToken, promise };
  promise
    .catch(() => {})
    .finally(() => {
      if (inflightRefresh?.promise === promise) {
        inflightRefresh = null;
      }
    });
  return promise;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => loadSession());
  const [isBootstrapping, setIsBootstrapping] = useState(Boolean(loadSession()));

  const applySession = useCallback((next) => {
    setSession(next);
    setAccessToken(next?.accessToken || null);
    persistSession(next);
  }, []);

  // On boot, if we have a refresh token, exchange it for fresh credentials so
  // the user doesn't have to re-login after a reload / Electron restart.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const existing = loadSession();
      if (!existing?.refreshToken) {
        setIsBootstrapping(false);
        return;
      }
      try {
        const result = await dedupedRefresh(existing.refreshToken);
        if (!cancelled) {
          applySession({
            user: result.user,
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          });
        }
      } catch {
        if (!cancelled) {
          applySession(null);
        }
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  const signIn = useCallback(
    async (email, password) => {
      const result = await apiLogin(email, password);
      applySession({
        user: result.user,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });
      return result.user;
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    const current = session;
    applySession(null);
    if (current?.refreshToken) {
      try {
        await apiLogout(current.refreshToken);
      } catch {
        // best-effort; local session is already cleared
      }
    }
  }, [session, applySession]);

  // When the API client gets a 401, drop the session so the router redirects to /login.
  useEffect(() => {
    setUnauthorizedHandler(() => applySession(null));
    return () => setUnauthorizedHandler(null);
  }, [applySession]);

  const value = useMemo(
    () => ({
      user: session?.user || null,
      roles: session?.user?.roles || [],
      permissions: session?.user?.permissions || {},
      isAuthenticated: Boolean(session?.accessToken),
      isBootstrapping,
      signIn,
      signOut,
    }),
    [session, isBootstrapping, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
